import { ReactElement, useEffect } from 'react'

import classNames from 'classnames'
import isEmpty from 'lodash/isEmpty'
import ReactGridLayout, { WidthProvider } from 'react-grid-layout'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { StringParam, useQueryParam } from 'use-query-params'

import {
    fetchAutomationCapabilitiesSuccess,
    fetchBrandGroupsSuccess,
} from 'actions/ui/app'
import {
    fetchDashboardSuccess,
    fetchOrganizationSuccess,
} from 'actions/ui/dashboardPrintPage'
import { Flex } from 'components/core'
import { InfoIcon, WhitelabelLogoIcon } from 'components/Icons'
import { LoadingIndicator } from 'components/LoadingIndicator'
import { UserBadge } from 'components/UserBadge/UserBadge'
import { date } from 'configuration/dayjs'
import { DATES } from 'const/filters'
import {
    WIDGET_GRID_ROW_HEIGHT,
    WIDGET_GRID_X_MARGIN,
    WIDGET_GRID_Y_MARGIN,
} from 'const/widgets'
import { PageContextProvider } from 'context/PageContext'
import { UserOverrideContextProvider } from 'context/UserOverrideContext'
import { changeCurrencyCode } from 'features/App/appService'
import { Widget } from 'features/Dashboards/components/Widgets'
import {
    printedWidgetRange,
    withPrintedRange,
} from 'features/Dashboards/printRange'
import { RumViewTracker } from 'features/Monitoring/Datadog/RumViewTracker'
import { getRenderedDateRange } from 'helpers/dateRange'
import { getOrganizationTheme } from 'helpers/organizationTheme'
import { useDenormalizedTabs, useUiDomainValue } from 'hooks'
import CobaltJSLogo from 'images/logo/CobaltJSLogo'
import { defaultDatesFilter } from 'reducers/ui/defaults'
import { Dashboard, DashboardTab, DateRangeFilter, Organization } from 'types'

import {
    usePrintableDashboardQuery,
    usePrintOrganizationQuery,
    usePrintBrandGroupsQuery,
    usePrintAutomationCapabilitiesQuery,
} from './api/dashboardPrintQueries'
import { useDateRangeQueryParam } from './localHooks'
import * as styles from './styles.scss'

const GridLayout = WidthProvider(ReactGridLayout)

interface DashboardPrintPageTabProps {
    tab: DashboardTab
    index: number
    dateRange?: DateRangeFilter
}

function DashboardPrintPageTab({
    tab,
    index,
    dateRange,
}: DashboardPrintPageTabProps): ReactElement {
    const tabDateRange =
        (tab.localFilters?.[DATES] as DateRangeFilter) ?? defaultDatesFilter

    const showFilterConflictIcon = dateRange && dateRange !== tabDateRange
    const printedTab = withPrintedRange(tab, dateRange)
    return (
        <div key={tab.id} className={styles['tab-section']}>
            {index > 0 && <div className={styles['page-break']} />}
            <div
                className={classNames(styles['tab-header'], {
                    'first-tab-header': index === 0,
                })}
            >
                <h2>{tab.name}</h2>
                <h3 className={styles['date-range']}>
                    {getRenderedDateRange(
                        dateRange ?? tabDateRange,
                        'MMMM D, YYYY'
                    )}
                    {showFilterConflictIcon && (
                        <InfoIcon className={styles['info-icon']} />
                    )}
                </h3>
            </div>
            <div className={styles['tab-content']}>
                <GridLayout
                    layout={tab.widgets.map((w) => w.layout)}
                    rowHeight={WIDGET_GRID_ROW_HEIGHT}
                    isDraggable={false}
                    isResizable={false}
                    containerPadding={[0, 0]}
                    margin={[WIDGET_GRID_X_MARGIN, WIDGET_GRID_Y_MARGIN]}
                >
                    {tab.widgets.map((widget) => (
                        <div key={widget.id}>
                            <Widget
                                className={classNames(styles.widget, {
                                    [styles.table]: widget.type === 'table',
                                })}
                                widget={widget}
                                widgetTab={printedTab}
                                isExport
                                hasPermission={false}
                                showFiltersDisplay
                                dateRange={printedWidgetRange(
                                    widget,
                                    dateRange
                                )}
                            />
                        </div>
                    ))}
                </GridLayout>
            </div>
        </div>
    )
}

function DashboardPrintPage(): ReactElement {
    const { dashboardId } = useParams<{ dashboardId: string }>()

    const [userId] = useQueryParam('user_id', StringParam)
    const [organizationId] = useQueryParam('org_id', StringParam)
    const [organizationGroupId] = useQueryParam('org_group_id', StringParam)
    const [currencyCode] = useQueryParam('currency_code', StringParam)
    const [dateRange] = useDateRangeQueryParam()

    const userIdOverride =
        (userId === 'localPreview' ? undefined : userId) ?? undefined

    const dispatch = useDispatch()

    const { data: fetchedDashboard, isPending: dashboardPending } =
        usePrintableDashboardQuery({
            dashboardId,
            userId: userIdOverride,
            orgId: organizationId,
            orgGroupId: organizationGroupId,
        })

    const { data: fetchedOrganization, isPending: organizationPending } =
        usePrintOrganizationQuery({
            organizationId: fetchedDashboard?.organization_id,
            userId: userIdOverride,
            orgGroupId: organizationGroupId,
        })

    const { data: fetchedBrandGroups, isPending: brandGroupsPending } =
        usePrintBrandGroupsQuery({
            userId: userIdOverride,
            orgId: organizationId,
            orgGroupId: organizationGroupId,
        })

    const {
        data: fetchedAutomationCapabilities,
        isPending: automationCapabilitiesPending,
    } = usePrintAutomationCapabilitiesQuery({
        userId: userIdOverride,
        orgId: organizationId,
        orgGroupId: organizationGroupId,
    })

    useEffect(() => {
        if (currencyCode) {
            changeCurrencyCode(currencyCode).catch(() => {})
        }
    }, [currencyCode])

    // The render path reads dashboard/organization/tabs from the page slice and
    // brand groups/automation capabilities from the app slice; mirror each query
    // result into those slices so the existing selectors keep resolving.
    useEffect(() => {
        if (fetchedDashboard) {
            dispatch(fetchDashboardSuccess(fetchedDashboard))
        }
    }, [dispatch, fetchedDashboard])

    useEffect(() => {
        if (fetchedOrganization) {
            dispatch(fetchOrganizationSuccess(fetchedOrganization))
        }
    }, [dispatch, fetchedOrganization])

    useEffect(() => {
        if (fetchedBrandGroups) {
            dispatch(fetchBrandGroupsSuccess(fetchedBrandGroups))
        }
    }, [dispatch, fetchedBrandGroups])

    useEffect(() => {
        if (fetchedAutomationCapabilities) {
            dispatch(
                fetchAutomationCapabilitiesSuccess(
                    fetchedAutomationCapabilities
                )
            )
        }
    }, [dispatch, fetchedAutomationCapabilities])

    const organization = useUiDomainValue<Organization>([
        'dashboardPrintPage',
        'organization',
    ])

    const dashboard = useUiDomainValue<Dashboard>([
        'dashboardPrintPage',
        'dashboard',
    ])

    const tabs = useDenormalizedTabs('dashboardPrintPage')

    // Gate rendering until every fetch has settled and its result is present in
    // the slices the render reads, so the PDF pipeline captures a complete page.
    const isBootstrapping =
        dashboardPending ||
        organizationPending ||
        brandGroupsPending ||
        automationCapabilitiesPending ||
        !dashboard ||
        !organization ||
        isEmpty(tabs)

    // Remove horizontal scroll bar on table widgets
    useEffect(() => {
        if (!isBootstrapping) {
            setTimeout(() => {
                const tables = document.querySelectorAll('.ant-table-body')
                tables.forEach((table) =>
                    table.setAttribute('style', 'overflow-x: hidden')
                )
            }, 100)
        }
    }, [isBootstrapping])

    if (isBootstrapping) {
        return <LoadingIndicator size="small" />
    }

    const theme = getOrganizationTheme(organization)
    const whiteLabelLogoUrl = theme?.logo_url_print

    return (
        <PageContextProvider pageName={'dashboardPrintPage'}>
            <RumViewTracker />
            <UserOverrideContextProvider
                userOverride={{
                    userId: userIdOverride,
                    organizationId: organizationId ?? undefined,
                    organizationGroupId: organizationGroupId ?? undefined,
                }}
            >
                <div id="dashboard-content" className={styles.container}>
                    <header>
                        <div>
                            <h1>{dashboard.name}</h1>
                        </div>
                        {whiteLabelLogoUrl ? (
                            <WhitelabelLogoIcon
                                src={whiteLabelLogoUrl}
                                alt={organization.name}
                                className={styles['org-logo']}
                            />
                        ) : (
                            <div className={styles['org-name']}>
                                {organization.name}
                            </div>
                        )}
                        <div
                            className="fg-text-light"
                            style={{ marginBottom: 12 }}
                        >
                            {dashboard.description}
                        </div>
                        <div className={styles.metadata}>
                            <span>Created by:</span>
                            <UserBadge email={dashboard.created_by} />
                        </div>
                        <div className={styles.metadata}>
                            <span>Exported:</span>
                            {date().format('MMM D, YYYY')}
                        </div>
                    </header>
                    {tabs.map((t, i) => (
                        <DashboardPrintPageTab
                            key={t.id}
                            tab={t}
                            index={i}
                            dateRange={dateRange}
                        />
                    ))}
                    <Flex
                        justifyContent="space-between"
                        component="footer"
                        alignItems="normal"
                    >
                        <div>
                            <div>{dashboard.name}</div>
                            <div>
                                {getRenderedDateRange(
                                    dateRange,
                                    'MMMM D, YYYY'
                                )}
                            </div>
                        </div>

                        <div className={styles['ds-icon']}>
                            <div className={styles.powered}>Powered By</div>
                            <CobaltJSLogo style={{ width: 130 }} />
                        </div>
                    </Flex>
                </div>
            </UserOverrideContextProvider>
        </PageContextProvider>
    )
}

export default DashboardPrintPage
